import React from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from 'react-router-dom';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';

import { useContext } from 'react';
import { UserContext } from './contexts/UserContext';

import Login from './pages/Login/Login';
import AllVisits from './pages/AllVisits/AllVisits';
import FFCReport from './pages/FFC/FFCReport';
import ARReport from './pages/ARReport/ARReport';
import AuthForm from './pages/AuthForm/AuthForm';
import RulesForm from './pages/RulesForm/RulesForm';
import LeadershipReport from './pages/Leadership/LeadershipReport';
import ArgusReport from './pages/Leadership/ArgusReport';
import HierarchyReport from './pages/HierarchyReport/HierarchyReport';
import VisitDetail from './pages/VisitDetail/VisitDetail';
import AuthenticatedLayout from './layouts/AuthenticatedLayout';

const ProtectedRoute = ({ children, allowedRoles = null }) => {
  const isAuthenticated = useIsAuthenticated();
  const { inProgress } = useMsal();
  const location = useLocation();
  const { user } = useContext(UserContext);

  if (inProgress !== InteractionStatus.None) return null;
  if (!isAuthenticated) {
    return <Navigate to="/" replace state={{ from: location }} />;
  }

  if (!allowedRoles) return children;

  if (!user) return null;

  const { roles } = user;
  const hasAccess = allowedRoles.some(role => roles?.[role]);

  if (!hasAccess) {
    return <Navigate to="/visits" replace />;
  }

  return children;
};

const App = () => {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<Login />} />

        <Route
          element={
            <ProtectedRoute>
              <AuthenticatedLayout />
            </ProtectedRoute>
          }
        >
          <Route path="/visits" element={<AllVisits />} />
          <Route path="/ffc-report" element={<FFCReport />} />
          <Route path="/ar-report" element={<ARReport />} />
          <Route
            path="/leadership-report"
            element={
              <ProtectedRoute allowedRoles={['is_admin', 'is_leadership']}>
                <LeadershipReport />
              </ProtectedRoute>
            }
          />
          <Route
            path="/hierarchy-report"
            element={
              <ProtectedRoute allowedRoles={['is_admin', 'is_leadership']}>
                <HierarchyReport />
              </ProtectedRoute>
            }
          />
          <Route
            path="/argus-report"
            element={
              <ProtectedRoute allowedRoles={['is_admin', 'is_leadership']}>
                <ArgusReport />
              </ProtectedRoute>
            }
          />

          <Route
            path="/auth-form"
            element={
              <ProtectedRoute allowedRoles={['is_admin']}>
                <AuthForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/rules-form"
            element={
              <ProtectedRoute allowedRoles={['is_admin']}>
                <RulesForm />
              </ProtectedRoute>
            }
          />
          <Route path="/visit/:unique_id" element={<VisitDetail />} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
};

export default App;